import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Users, Search, Check, X } from 'lucide-react';

/**
 * Wave 3.78 — Per-stage delay-notify user picker.
 *
 * Renders a compact "n users" pill that opens a popover with a searchable
 * checklist of company users. Any number of users can be selected per stage.
 * ``disabled`` freezes the picker (used for stages that already completed).
 */
export function StageNotifyPicker({
  users = [],
  value = [],
  onChange,
  disabled = false,
  testId = 'stage-notify-picker',
  ariaLabel = 'Select users to notify on delay',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = (q || '').toLowerCase().trim();
    if (!needle) return users;
    return users.filter((u) => {
      const hay = `${u.name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  const selectedCount = (value || []).length;
  const selectedUsers = users.filter((u) => (value || []).includes(u.id));
  const previewNames = selectedUsers.map((u) => (u.name || u.email || '').trim()).join(', ');

  const _initials = (u) => {
    const n = (u.name || u.email || '?').trim();
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (n.slice(0, 2) || '?').toUpperCase();
  };
  const _colorFor = (u) => {
    // Deterministic tint per user id — safe brand-adjacent palette.
    const palette = ['#0B1B2B', '#0F766E', '#B08D57', '#1E4E8C', '#5B21B6', '#7C2D12', '#065F46', '#831843'];
    const s = (u.id || u.email || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return palette[s % palette.length];
  };

  const toggle = (uid) => {
    if (disabled) return;
    const cur = Array.isArray(value) ? value : [];
    if (cur.includes(uid)) onChange(cur.filter((x) => x !== uid));
    else onChange([...cur, uid]);
  };

  const clearAll = () => onChange([]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          title={selectedCount === 0 ? (disabled ? 'Locked — stage already completed' : 'Add users to notify') : `${selectedCount} user${selectedCount === 1 ? '' : 's'}: ${previewNames}`}
          className={`inline-flex items-center gap-1.5 h-8 px-2 rounded-md border text-[11px] max-w-full transition-colors duration-150
            ${disabled
              ? 'bg-slate-50 text-slate-400 border-[var(--divider)] cursor-not-allowed'
              : selectedCount > 0
                ? 'bg-[var(--paper-warm)] text-[var(--ink-navy)] border-[var(--divider)] hover:border-[var(--gold-accent)]'
                : 'bg-white text-slate-500 border-[var(--divider)] hover:border-[var(--gold-accent)]'
            }`}
          data-testid={testId}
        >
          {selectedCount === 0 ? (
            <>
              <Users size={12} className="shrink-0" />
              <span className="truncate">{disabled ? 'Locked' : 'Add users'}</span>
            </>
          ) : (
            <>
              <span className="flex -space-x-1 shrink-0">
                {selectedUsers.slice(0, 3).map((u) => (
                  <span
                    key={u.id}
                    aria-hidden="true"
                    className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-medium ring-1 ring-white"
                    style={{ background: _colorFor(u), color: 'white' }}
                  >
                    {_initials(u)}
                  </span>
                ))}
              </span>
              <span className="truncate font-mono tabular-nums text-[11px] text-[var(--ink-navy)]">
                {selectedCount}
              </span>
              {selectedCount > 3 && (
                <span className="text-[10px] text-slate-500">+{selectedCount - 3} more</span>
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid={`${testId}-popover`}>
        <div className="p-2 border-b border-[var(--divider)]">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search users…"
              className="pl-7 h-8 text-[12px]"
              data-testid={`${testId}-search`}
            />
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-[11px] text-slate-500 py-4 text-center">No users match.</div>
          ) : filtered.map((u) => {
            const checked = (value || []).includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left"
                data-testid={`${testId}-item-${u.email || u.id}`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0
                    ${checked ? 'bg-[var(--ink-navy)] border-[var(--ink-navy)] text-white' : 'bg-white border-slate-300'}`}
                >
                  {checked && <Check size={10} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-[var(--ink-navy)] truncate">{u.name || u.email}</div>
                  <div className="text-[10px] text-slate-500 truncate">{u.email}{u.role ? ` · ${u.role.replace('_', ' ')}` : ''}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between p-2 border-t border-[var(--divider)]">
          <span className="text-[10px] text-slate-500">{selectedCount} selected</span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={selectedCount === 0}
              className="h-7 text-[11px]"
              data-testid={`${testId}-clear`}
            >
              <X size={11} className="mr-0.5" /> Clear
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setOpen(false)}
              className="h-7 text-[11px]"
              data-testid={`${testId}-done`}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
